/**
 * Collapses consecutive slashes in a path string while optionally preserving a single trailing slash.
 * Does not resolve dot segments ('.' or '..'); it only collapses repeated '/' in-place.
 */
const collapseSlashes = (path: string, keepTrailing: boolean): string => {
  if (path.length === 0) return path;

  let out = '';
  let prevSlash = false;

  for (let i = 0; i < path.length; i++) {
    const code = path.charCodeAt(i); // '/'
    if (code === 47) {
      if (!prevSlash) out += '/';
      prevSlash = true;
    } else {
      out += path[i];
      prevSlash = false;
    }
  }

  if (keepTrailing && !out.endsWith('/')) out += '/';
  if (!keepTrailing && out.length > 1 && out.endsWith('/')) out = out.slice(0, -1);

  return out;
};

/**
 * Splits a URL reference into { path, suffix } where suffix is the query and/or fragment part.
 * Works for absolute or relative references without validating them.
 */
const splitPathAndSuffix = (input: string): { path: string; suffix: string } => {
  const q = input.indexOf('?');
  const h = input.indexOf('#');

  let cut = input.length;
  if (q !== -1 && h !== -1) cut = Math.min(q, h);
  else if (q !== -1) cut = q;
  else if (h !== -1) cut = h;

  return { path: input.slice(0, cut), suffix: input.slice(cut) };
};

interface NormalizeUrlPathOptions {
  /**
   * Controls trailing slash behavior.
   * - `auto` (default): Preserve the original trailing-slash semantics.
   * - `true`: Force a trailing slash at the end of the normalized path.
   * - `false`: Ensure no trailing slash (except single root slash).
   */
  preserveTrailing?: 'auto' | boolean;
}

/**
 * Normalizes a URL or URL path by collapsing duplicate slashes and optionally preserving trailing slashes.
 * Handles both absolute URLs and standalone path strings, ensuring consistency in formatting.
 *
 * If an invalid URL is provided, it attempts to normalize it as a standalone path and suffix.
 * For network-path references (e.g., `//host/...`), a base URL is required to normalize safely.
 *
 * @param {string} input - The URL or path to normalize.
 * @param {NormalizeUrlPathOptions} [options={}] - Optional configuration settings.
 * @param {boolean | 'auto'} [options.preserveTrailing='auto'] - Determines how trailing slashes are preserved:
 * - `'auto'`: Preserves trailing slashes only if they were present in the input.
 * - `true`: Always preserves trailing slashes.
 * - `false`: Always removes trailing slashes.
 *
 * @returns {string} The normalized URL or path string.
 *
 * @throws {TypeError} If the input is a network-path reference (e.g., `//host/...`) without a base URL.
 *
 * @example
 * // Normalizing a full URL with collapsing slashes:
 * normalizeUrlPath('https://example.com//path//to///resource/');
 * // Returns: 'https://example.com/path/to/resource/'
 *
 * @example
 * // Normalizing a standalone path while preserving trailing slashes:
 * normalizeUrlPath('/path//to////resource/', { preserveTrailing: true });
 * // Returns: '/path/to/resource/'
 *
 * @example
 * // Providing an invalid URL with a suffix:
 * normalizeUrlPath('/some//nested////path.txt');
 * // Returns: '/some/nested/path.txt'
 *
 * @example
 * // Throwing on a network-path reference:
 * normalizeUrlPath('//example.com//some/path');
 * // Throws: TypeError('Network-path references (//host/...) require a base URL to normalize safely')
 *
 * @remarks
 * - This function uses the `URL` API to handle absolute URLs (e.g., those beginning with `http://`, `https://`, etc.).
 * - For standalone paths, it processes the input by splitting the path and suffix (e.g., file extensions) and normalizing slashes.
 * - The `collapseSlashes` and `splitPathAndSuffix` helper functions are used internally to handle the details of path processing.
 */
export const normalizeUrlPath = (input: string, options: NormalizeUrlPathOptions = {}): string => {
  const preserve = options.preserveTrailing ?? 'auto';

  try {
    const url = new URL(input);
    const hadTrailing = url.pathname.endsWith('/');
    const keep = preserve === 'auto' ? hadTrailing : Boolean(preserve);
    url.pathname = collapseSlashes(url.pathname, keep);
    return url.toString();
  } catch {
    const { path, suffix } = splitPathAndSuffix(input);

    if (path.startsWith('//')) {
      throw new TypeError('Network-path references (//host/...) require a base URL to normalize safely');
    }

    const hadTrailing = path.length > 0 && path.endsWith('/');
    const keep = preserve === 'auto' ? hadTrailing : Boolean(preserve);
    const normalizedPath = collapseSlashes(path, keep);
    return normalizedPath + suffix;
  }
};
